"use client"

import { useEffect, useState } from "react"

import {
  buildChartSeries,
  buildPriceChartSeries,
  buildScoreTrendSeries,
  type ChartPoint,
} from "../lib/chart-series"

type LiveChartSeriesOptions = {
  points?: number
  base?: number
  min?: number
  max?: number
  stepAmplitude?: number
  decimals?: number
  refreshMs?: number
}

export function useLiveChartSeries(
  seedKey: string,
  options: LiveChartSeriesOptions = {}
) {
  const {
    points = 12,
    base = 50,
    min = 5,
    max = 95,
    stepAmplitude = 8,
    decimals = 1,
    refreshMs = 8000,
  } = options

  const [data, setData] = useState<ChartPoint[]>(() =>
    buildChartSeries(seedKey, {
      points,
      base,
      min,
      max,
      stepAmplitude,
      decimals,
    })
  )

  useEffect(() => {
    function refresh() {
      setData(
        buildChartSeries(seedKey, {
          points,
          base,
          min,
          max,
          stepAmplitude,
          decimals,
          tick: Date.now(),
        })
      )
    }

    refresh()
    const id = window.setInterval(refresh, refreshMs)
    return () => window.clearInterval(id)
  }, [seedKey, points, base, min, max, stepAmplitude, decimals, refreshMs])

  return data
}

export function useLivePriceChartSeries(seedKey: string, refreshMs = 8000) {
  const [data, setData] = useState<ChartPoint[]>(() =>
    buildPriceChartSeries(seedKey)
  )

  useEffect(() => {
    function refresh() {
      setData(buildPriceChartSeries(seedKey, { tick: Date.now() }))
    }

    refresh()
    const id = window.setInterval(refresh, refreshMs)
    return () => window.clearInterval(id)
  }, [seedKey, refreshMs])

  return data
}

export function useLiveScoreTrendSeries(seedKey: string, refreshMs = 8000) {
  const [data, setData] = useState<ChartPoint[]>(() =>
    buildScoreTrendSeries(seedKey)
  )

  useEffect(() => {
    function refresh() {
      setData(buildScoreTrendSeries(seedKey, { tick: Date.now() }))
    }

    refresh()
    const id = window.setInterval(refresh, refreshMs)
    return () => window.clearInterval(id)
  }, [seedKey, refreshMs])

  return data
}
